import { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { Modal, ModalDialog, ModalClose, Typography, Stack, Button, Box, Alert } from '@mui/joy';
import { useBulkAddMutation } from '../api';
import { ApiError } from '@/components/ApiError';
import { ShoppingListItems } from './ShoppingListItems';
import { FullScreenLoader } from '@/components/FullScreenLoader';

export function AddToShoppingListModal({ verb = 'Add to', source, data, isLoading, error }) {
  const { householdId } = useParams();
  const navigate = useNavigate();
  
  const entries = useMemo(() => (data?.data || []).map((entry, index) => ({ ...entry, id: index })), [data]);

  const [selectedEntries, setSelectedEntries] = useState(new Set());
  const [bulkAdd, { isLoading: isAdding }] = useBulkAddMutation();

  useEffect(() => {
    handleSelectAll();
  }, [entries]);

  const handleToggleEntry = (entry) => {    
    const newSelected = new Set(selectedEntries);
    if (newSelected.has(entry.id)) {
      newSelected.delete(entry.id);
    } else {
      newSelected.add(entry.id);
    }
    setSelectedEntries(newSelected);
  };

  const handleSelectAll = () => {    
    setSelectedEntries(new Set(entries.map((_, index) => index)));    
  };

  const handleDeselectAll = () => {
    setSelectedEntries(new Set());
  };

  const handleAddSelected = async () => {
    const entriesToAdd = entries.filter((_, index) => selectedEntries.has(index));
    
    try {
      await bulkAdd({ householdId, entries: entriesToAdd });      
      navigate(`/h/${householdId}/shopping-list`);
    } catch (error) {
      console.error('Failed to add items:', error);
    }
  };

  const handleClose = () => {
    navigate(-1);
  };

  const allSelected = entries.length > 0 && selectedEntries.size === entries.length;
  const someSelected = selectedEntries.size > 0;

  return (
    <Modal open={true} onClose={handleClose}>
      <ModalDialog sx={{ maxWidth: '700px', width: '90%', height: '90vh', display: 'flex', flexDirection: 'column', pb: 0}}>
        <ModalClose />
        {isLoading ? <FullScreenLoader /> : (
           <Stack gap={2} direction="column" sx={{ flex: 1, minHeight: 0 }}>
           <Stack direction="column" gap={0.5} sx={{ mb: 1 }}>
             <Typography level="h4">
               {verb} Shopping List
             </Typography>
             <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>From {source}</Typography>
           </Stack>
                       
           <Stack direction="row" gap={1} justifyContent="space-between">
            <Button variant="outlined" color="neutral" 
              onClick={allSelected ? handleDeselectAll : handleSelectAll}               
              sx={{ width: '115px' }}            
             >
               {allSelected ? 'Deselect All' : 'Select All'}
             </Button>
             <Button 
               onClick={handleAddSelected}
               disabled={!someSelected || isAdding}
               loading={isAdding}
               sx={{ flex: { xs: 1, sm: '0 0 auto' } }}
             >
               Add Selected ({selectedEntries.size})
             </Button>
           </Stack>           
 
           <Alert severity="info">Choose items you want to add to your shopping list</Alert>
           {error ? (
             <ApiError error={error} />
           ) : (            
             <Box sx={{ flex: 1, overflowY: 'auto', pb: 1, minHeight: 0, mx: -1.5, px: 1.5 }}>
              <ShoppingListItems
                items={entries}
                crossOffSelected={false}
                isEntryChecked={(entry) => selectedEntries.has(entry.id)}
                onToggle={handleToggleEntry}
                showRecipeSources />
             </Box>
           )}
         </Stack>
        )}       
      </ModalDialog>
    </Modal>
  );
}

